import React from "react";
import PropTypes from "prop-types";
import {
  NavigatorAction,
  NavigatorStore,
  Dispatcher,
  BookUtils
} from "../../../../flux";
import { Select } from "@vitalsource/vstui";

export default class ContentsSelection extends React.Component {
  constructor(props) {
    super(props);
    const reading = this.props.reading;
    const state = {
      ready: !!(reading.start && reading.end && reading.description),
      start: reading.start,
      end: reading.end,
      summary: reading.summary,
      description: reading.description,
      order: reading.order || 1,
      type: reading.type || this.props.book.format,
      saving: false,
      exclusive: false,
      rangeType: this.props.book.format === "pbk" ? "page_index" : "cfi",
      selected: "none"
    };
    this.state = Object.assign(state, this.props.savedState);
  }

  componentDidMount() {
    this.focusTitle.focus();
    if (this.state.start) {
      this.props.book.goTo({ cfi: this.state.start });
    }
    if (this.props.book.hasPageBreaks) {
      this.loadChapters();
      this.loadTOC();
    } else {
      Dispatcher.bindOnce(
        Dispatcher.BOOK_PAGEBREAKS_LOADED,
        this.loadChapters.bind(this)
      );
    }
  }

  loadChapters() {
    const chapterRanges = BookUtils.getChapterRanges(this.props.isbn);
    let pageEnds = chapterRanges.map(chapter => chapter.pop());
    this.setState({ pageEnds });
  }

  loadTOC() {
    const toc = this.props.book.getTOC().filter(chapter => chapter.level === 1);
    const lastDocument = BookUtils.getLastDocument(this.props.book.isbn);
    const reading = this.props.reading;
    const selected = toc.findIndex(
      chapter => chapter.cfi === this.props.reading.start
    );
    this.setState({
      toc,
      lastDocument,
      selected: selected > -1 ? selected : "none"
    });
  }

  back() {
    NavigatorAction.pop();
    const newState = {};
    newState[this.props.route] = null;
    this.props.saveState(newState);
  }

  _createReading() {
    if (this.state.ready) {
      const meta = [{ tooling: "contents-selection" }];
      if (this.props.assignment.uuid) {
        const startCfi = this.state.start;
        const saveReading = this._createOrUpdateReading.bind(this, meta);
        this.setState({ saving: true });
        this.props.book.goTo({
          cfi: this.state.start,
          callback: () => {
            if (startCfi.indexOf("!") === -1) {
              this._savePreview("/4", saveReading);
            } else {
              this._savePreview(startCfi.split("!").pop(), saveReading);
            }
          }
        });
      } else {
        this.props
          .createOrUpdateAssignment()
          .then(this._createReading.bind(this));
      }
    }
  }

  _createOrUpdateReading(meta) {
    if (!this.props.saving && this.props.feenyReady) {
      const reading = {
        start: this.state.start,
        end: this.state.end,
        summary: this.state.summary,
        description: this.state.description || undefined,
        order: this.state.order,
        type: this.state.type,
        exclusive: this.state.exclusive,
        range_type: this.state.rangeType
      };
      if (this.props.reading && this.props.reading.uuid) {
        this.props.updateReading(this.props.reading.uuid, reading);
      } else {
        this.props.createReading(reading, meta);
      }
      this.back();
    }
  }

  _onChangeChapter(event) {
    const chapterIndex = event.target.value;
    if (chapterIndex !== "none") {
      const chapterInt = parseInt(chapterIndex);
      const startCFI = this.state.toc[chapterInt].cfi;
      const nextChapter = this.state.toc[chapterInt + 1] || {cfi: BookUtils.nextDoc(this.state.lastDocument.cfi)};
      const [endCFI, _] = nextChapter.cfi.split("!");
      this.setState(
        {
          ready: false,
          start: startCFI,
          end: this.props.book.format === "pbk" ? endCFI : `${endCFI}!`,
          selected: chapterIndex,
          exclusive: true,
          description: $("#toc-select option:selected")
            .text()
            .trim()
            .substring(0, 255)
        },
        () => {
          const newState = {};
          newState[this.props.route] = this.state;
          this.props.saveState(newState);
          this.props.book.viewer.Book.goToCFI(startCFI, () =>
            this.setState({ ready: true })
          );
        }
      );
    } else {
      this.setState({
        ready: false,
        start: undefined,
        end: undefined,
        selected: chapterIndex,
        description: ""
      });
    }
  }

  _savePreview(cfi, callback) {
    this.props.book.viewer.Book.getPreview(cfi, response => {
      this.setState({ saving: false });
      if (response.data) {
        this.setState(
          {
            summary: response.data.preview,
            saving: false
          },
          () => {
            const newState = {};
            newState[this.props.route] = this.state;
            this.props.saveState(newState);
            if (typeof callback === "function") {
              callback();
            }
          }
        );
      }
    });
  }

  _onDescriptionChange(description) {
    const ready = !!this.state.start;
    this.setState(
      {
        description: description.substring(0, 255),
        ready
      },
      () => {
        const newState = {};
        newState[this.props.route] = this.state;
        this.props.saveState(newState);
      }
    );
  }

  renderPages() {
    let tocArray = [];
    if (this.state.toc) {
      tocArray = this.state.toc.map((chapter, index) => (
        <option key={index} value={index} title={chapter.title.capitalize()}>
          {chapter.title.capitalize().substring(0, 47)}
          {chapter.title.length > 50 && "..."}
        </option>
      ));
    }
    return [
      <option value="none">{i18n.t("assignments.select-chapter")}</option>
    ].concat(tocArray);
  }

  render() {
    const placeholderExample = i18n.t("assignments.start-end-ie");

    return (
      <div id="reading-new">
        <div className="instructions">
          <h3
            className="title"
            ref={select => {
              this.focusTitle = select;
            }}
            tabIndex={-1}
          >
            {i18n.t("assignments.contents-selection")}
          </h3>
        </div>

        <div className="reading-form">
          <div className="input-container">
            <Select
              id="toc-select"
              label={i18n.t("reader.panels.toc")}
              value={this.state.selected}
              onChange={this._onChangeChapter.bind(this)}
            >
              {this.renderPages()}
            </Select>
          </div>
          <div className="input-container">
            <label htmlFor="reading-name" className="label">
              {i18n.t("assignments.reading-name")} (
              {i18n.t("base.optional").toLowerCase()})
            </label>
            <input
              className="input"
              type="text"
              name="reading-name"
              id="reading-name"
              value={this.state.description}
              maxLength={255}
              placeholder={placeholderExample}
              onChange={event => this._onDescriptionChange(event.target.value)}
            />
          </div>
          <div className="form-actions">
            <button
              className={`${
                this.state.ready && !this.state.saving
                  ? "submit"
                  : "transparentButton"
              }`}
              disabled={!this.state.ready && this.state.saving}
              onClick={this._createReading.bind(this)}
            >
              {this.state.saving
                ? `${i18n.t("base.saving")}...`
                : i18n.t("base.save")}
            </button>
            <button className="cancel noButton" onClick={() => this.back()}>
              {i18n.t("base.cancel")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

ContentsSelection.propTypes = {
  isbn: PropTypes.string.isRequired,
  book: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  createReading: PropTypes.func.isRequired,
  createOrUpdateAssignment: PropTypes.func.isRequired,
  assignment: PropTypes.object,
  reading: PropTypes.object
};

ContentsSelection.defaultProps = {
  reading: {}
};
